import React, { useState, useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Phone, Send, CheckCircle2, AlertTriangle, Star, X, ArrowDown } from "lucide-react";
import SEO from "../components/SEO";
import { api, formatApiError } from "../lib/api";
import { allSuburbs, PHONE, PHONE_TEL } from "../data/content";
import { trackEvent } from "../lib/analytics";

export default function Book() {
  const [searchParams] = useSearchParams();
  const prefillService = searchParams.get("service") || "";
  const prefillSuburb = searchParams.get("suburb") || "";

  const [form, setForm] = useState({
    name: "", phone: "", email: "",
    suburb: prefillSuburb,
    message: prefillService ? `Hi — I'd like to book the same service as last time: ${prefillService}.` : "",
  });
  const [status, setStatus] = useState({ state: "idle", message: "" });
  const [showSuccessModal, setShowSuccessModal] = useState(false);

  // Lock body scroll while modal open + close on Esc
  useEffect(() => {
    if (!showSuccessModal) return;
    document.body.style.overflow = "hidden";
    const onKey = (e) => { if (e.key === "Escape") setShowSuccessModal(false); };
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", onKey);
    };
  }, [showSuccessModal]);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setStatus({ state: "loading", message: "" });
    try {
      const payload = Object.fromEntries(
        Object.entries(form).filter(([, v]) => v !== "" && v !== null && v !== undefined)
      );
      await api.post("/bookings", payload);
      setStatus({ state: "success", message: "Enquiry received. We'll call you back shortly." });
      trackEvent("enquiry_submitted", { suburb: form.suburb || "unspecified" });
      setShowSuccessModal(true);
      setForm({
        name: "", phone: "", email: "", suburb: "", message: "",
      });
    } catch (err) {
      setStatus({ state: "error", message: formatApiError(err?.response?.data?.detail) || "Submission failed. Please call us." });
    }
  };

  return (
    <>
      <SEO
        title="Send an Enquiry | On-Site IT Service Adelaide — Tech Emergency"
        description="Send an enquiry for a same-day or scheduled on-site IT visit anywhere across Adelaide. Computer repairs, WiFi, email, cyber security. Or call 1800 836 390."
        path="/book"
      />

      <section className="pt-6 lg:pt-20 pb-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-12 gap-6 lg:gap-12">

          {/* Mobile-first compact hero — short, with form anchor */}
          <div className="lg:col-span-5 lg:order-1">
            <div className="te-overline mb-3">/ Send an enquiry</div>
            <h1 className="text-3xl sm:text-4xl font-bold tracking-tighter leading-none mb-4">
              Tell us what's<br />broken.
            </h1>

            {/* Mobile-only scroll cue — clear signal that the form is below */}
            <a
              href="#booking-form"
              className="lg:hidden flex items-center justify-center gap-2 w-full py-3 mb-2 border border-white/15 text-sm text-zinc-300 hover:border-[var(--te-primary)] hover:text-white transition-colors"
              data-testid="book-scroll-to-form"
            >
              Fill out the quick form below
              <ArrowDown size={14} className="animate-bounce" />
            </a>

            {/* Desktop-only extra copy + supporting blocks */}
            <p className="hidden lg:block text-zinc-400 leading-relaxed mt-6 mb-8">
              Submit the form and we'll call you back within business hours to confirm a time. For
              urgent issues right now, the phone is faster.
            </p>
            <div className="hidden lg:block te-card p-6 mt-8">
              <div className="te-overline mb-3">/ What happens next</div>
              <ol className="text-sm text-zinc-400 space-y-3">
                <li><span style={{ color: "var(--te-primary)" }}>01</span> &nbsp; We call you back to confirm details.</li>
                <li><span style={{ color: "var(--te-primary)" }}>02</span> &nbsp; You get an upfront quote at our transparent hourly rates.</li>
                <li><span style={{ color: "var(--te-primary)" }}>03</span> &nbsp; Your police-checked technician arrives.</li>
                <li><span style={{ color: "var(--te-primary)" }}>04</span> &nbsp; We fix it and explain what happened.</li>
                <li><span style={{ color: "var(--te-primary)" }}>05</span> &nbsp; Easy Payment Onsite — EFTPOS or cash payment upon job completion.</li>
              </ol>
            </div>
          </div>

          <div id="booking-form" className="lg:col-span-7 lg:order-2 scroll-mt-24">
            <form onSubmit={submit} className="te-card p-6 md:p-8 space-y-5" data-testid="booking-form">
            <p
              className="text-sm md:text-base text-zinc-300 px-4 py-3 border border-white/10"
              data-testid="booking-area-note"
            >
              Not sure if we service your area? Contact us to discuss your location and what you need help with.
            </p>
              <div className="grid sm:grid-cols-2 gap-5">
                <Field label="Your name" required>
                  <input data-testid="book-name" required value={form.name} onChange={update("name")} className="te-input" placeholder="Jane Smith" />
                </Field>
                <Field label="Phone" required>
                  <input data-testid="book-phone" required type="tel" value={form.phone} onChange={update("phone")} className="te-input" placeholder="04xx xxx xxx" />
                </Field>
              </div>

              <Field label="Email (optional)">
                <input data-testid="book-email" type="email" value={form.email} onChange={update("email")} className="te-input" placeholder="you@example.com" />
              </Field>

              <Field label="Suburb" required>
                <input
                  data-testid="book-suburb"
                  required
                  list="suburbs-list"
                  value={form.suburb}
                  onChange={update("suburb")}
                  className="te-input"
                  placeholder="e.g. Glenelg"
                />
                <datalist id="suburbs-list">
                  {allSuburbs.map((s) => <option key={s.slug} value={s.name} />)}
                </datalist>
                <p className="text-xs text-zinc-500 mt-1.5" data-testid="book-coverage-note">
                  We cover metro Adelaide and the Hills. Not sure about your area? Send the form through anyway — we'll confirm when we call you back.
                </p>
              </Field>

              <Field label="Describe the issue">
                <textarea data-testid="book-message" rows={4} value={form.message} onChange={update("message")} className="te-input resize-none" placeholder="What's happening? Any error messages? When did it start?" />
              </Field>

              {status.state === "error" && (
                <div className="flex items-start gap-3 p-4 border" style={{ borderColor: "var(--te-primary)", background: "rgba(255,59,48,0.08)" }} data-testid="book-error">
                  <AlertTriangle size={20} style={{ color: "var(--te-primary)" }} className="mt-0.5" />
                  <div className="text-sm">
                    <div className="font-semibold mb-1" style={{ color: "var(--te-primary)" }}>Couldn't submit</div>
                    <div className="text-zinc-300">{status.message}</div>
                  </div>
                </div>
              )}

              <div className="flex flex-col sm:flex-row gap-3 pt-2">
                <button
                  type="submit"
                  disabled={status.state === "loading"}
                  data-testid="book-submit-btn"
                  className="te-btn te-btn-primary flex-1 disabled:opacity-50"
                >
                  {status.state === "loading" ? "Submitting…" : (<><Send size={16} /> Submit booking</>)}
                </button>
                <a href={PHONE_TEL} className="te-btn te-btn-ghost" data-testid="book-form-call-btn">
                  <Phone size={16} /> Or call {PHONE}
                </a>
              </div>
            </form>
          </div>
        </div>
      </section>

      <style>{`
        .te-input {
          width: 100%;
          background: var(--te-surface);
          border: 1px solid var(--te-border);
          color: var(--te-text);
          padding: 0.75rem 0.875rem;
          font-family: 'IBM Plex Sans', sans-serif;
          font-size: 0.95rem;
          outline: none;
          transition: border-color 0.15s ease;
        }
        .te-input:focus { border-color: var(--te-primary); }
        .te-input::placeholder { color: var(--te-text-muted); }

        @keyframes te-modal-fade-in {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes te-modal-pop-in {
          0%   { opacity: 0; transform: translateY(20px) scale(0.96); }
          100% { opacity: 1; transform: translateY(0) scale(1); }
        }
        @keyframes te-check-pop {
          0%   { transform: scale(0); opacity: 0; }
          60%  { transform: scale(1.2); opacity: 1; }
          100% { transform: scale(1); opacity: 1; }
        }
      `}</style>

      {/* SUCCESS MODAL */}
      {showSuccessModal && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center p-4"
          style={{ background: "rgba(0,0,0,0.85)", backdropFilter: "blur(6px)", animation: "te-modal-fade-in 0.25s ease both" }}
          onClick={() => setShowSuccessModal(false)}
          data-testid="book-success-modal"
        >
          <div
            className="relative max-w-md w-full border border-white/10 overflow-hidden"
            style={{
              background: "var(--te-paper)",
              animation: "te-modal-pop-in 0.35s cubic-bezier(0.16, 1, 0.3, 1) both",
            }}
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-labelledby="success-modal-title"
          >
            {/* Close button */}
            <button
              onClick={() => setShowSuccessModal(false)}
              aria-label="Close"
              data-testid="book-success-modal-close"
              className="absolute top-4 right-4 w-9 h-9 flex items-center justify-center text-zinc-400 hover:text-white hover:bg-white/5 transition-colors"
            >
              <X size={18} />
            </button>

            {/* Animated red glow */}
            <div
              className="absolute -top-20 left-1/2 -translate-x-1/2 w-80 h-80 rounded-full opacity-30 blur-3xl pointer-events-none"
              style={{ background: "var(--te-primary)" }}
              aria-hidden="true"
            />

            <div className="relative p-8 md:p-10 text-center">
              {/* Animated tick */}
              <div
                className="w-16 h-16 mx-auto mb-6 flex items-center justify-center rounded-full"
                style={{ background: "var(--te-primary)", animation: "te-check-pop 0.5s cubic-bezier(0.18, 1.5, 0.4, 1) both" }}
              >
                <CheckCircle2 size={32} color="#fff" strokeWidth={2.5} />
              </div>

              <div className="te-overline mb-2" style={{ color: "var(--te-primary)" }}>/ ENQUIRY RECEIVED</div>
              <h2
                id="success-modal-title"
                className="text-3xl sm:text-4xl font-bold tracking-tighter leading-tight mb-4 text-white"
              >
                Thanks — we'll be<br />in touch shortly.
              </h2>
              <p className="text-zinc-400 leading-relaxed mb-8">
                Your request landed safely. One of our Adelaide team will call you back
                during business hours to confirm a time. For anything urgent right now,
                the phone is faster.
              </p>

              <div className="flex flex-col gap-3">
                <a
                  href={PHONE_TEL}
                  data-testid="book-success-modal-call"
                  className="te-btn te-btn-primary w-full"
                >
                  <Phone size={16} /> Call us · {PHONE}
                </a>
                <button
                  onClick={() => setShowSuccessModal(false)}
                  data-testid="book-success-modal-dismiss"
                  className="te-btn te-btn-ghost w-full"
                >
                  Got it
                </button>
              </div>

              {/* Review nudge */}
              <div className="mt-6 pt-6 border-t border-white/10">
                <div className="text-xs text-zinc-500 mb-2">
                  Already a happy customer from a previous job?
                </div>
                <Link
                  to="/review"
                  data-testid="book-success-modal-review"
                  onClick={() => setShowSuccessModal(false)}
                  className="inline-flex items-center gap-1.5 text-sm font-medium hover:underline"
                  style={{ color: "var(--te-primary)" }}
                >
                  <Star size={14} fill="currentColor" stroke="none" />
                  Leave us a quick Google review →
                </Link>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function Field({ label, required, children }) {
  return (
    <label className="block">
      <div className="te-overline text-[10px] mb-2">
        {label}{required && <span style={{ color: "var(--te-primary)" }}> *</span>}
      </div>
      {children}
    </label>
  );
}
